import type { Resource, ResourceLanguage } from 'i18next';
import { getI18n } from './i18n';

const DEFAULT_NS = 'translation';

/**
 * 이미 등록된 resource 추적용 cache.
 *
 * key 형식:
 * ko:translation
 * en:translation
 * ko:common
 */
const registeredResourceKeys = new Set<string>();

export type ResourceMap = Record<string, Record<string, Record<string, string>>>;

/**
 * 리소스 맵 생성 처리 옵션 타입
 */
export interface RegisterResourcesOptions {
    /**
     * 적용할 market.
     *
     * 예: kr, global, vn 등
     */
    market: string;
}

/**
 * feature package에서 사용할 flat resource 구조.
 *
 * 예:
 * {
 *   ko: {
 *     'button.save': '저장',
 *     'button.cancel': '취소'
 *   },
 *   en: {
 *     'button.save': 'Save',
 *     'button.cancel': 'Cancel'
 *   }
 * }
 */
export interface FlatResources {
    [locale: string]: {
        [key: string]: string;
    };
}

export interface RegisterFlatI18nResourcesOptions {
    /**
     * locale별 flat resource.
     */
    resources?: FlatResources;

    /**
     * 등록할 namespace.
     *
     * 기본값: translation
     */
    ns?: string;

    /**
     * 기존 값이 있을 때 덮어쓸지 여부.
     *
     * 기본값: true
     */
    overwrite?: boolean;
}

export interface RegisterI18nResourcesOptions {
    /**
     * i18next namespace resource 구조.
     *
     * 예:
     * {
     *   ko: {
     *     translation: { hello: '안녕하세요' },
     *     common: { save: '저장' }
     *   }
     * }
     */
    resources?: Resource;

    /**
     * 기존 값이 있을 때 덮어쓸지 여부.
     *
     * 기본값: true
     */
    overwrite?: boolean;
}

/**
 * resource 등록을 위한 createResourceMap 생성 함수.
 */
export const createResourceMap = (
    modules: Record<string, { default: Record<string, string> }>,
): ResourceMap => {
    const resourceMap: ResourceMap = {};

    Object.entries(modules).forEach(([path, module]) => {
        const match = path.match(/resources\/([^/]+)\/([^/]+)\/(.+)\.json$/);

        if (!match) {
            return;
        }

        const [, market, locale, namespace] = match;

        resourceMap[market] ??= {};
        resourceMap[market][locale] ??= {};

        const lastFileName = namespace.split('/').pop()!;

        Object.entries(module.default).forEach(([key, value]) => {
            if (namespace === 'translation') {
                resourceMap[market][locale][key] = value;
            } else {
                resourceMap[market][locale][`${lastFileName}.${key}`] = value;
            }
        });
    });

    return resourceMap;
};

/**
 * namespace 구조 resource 등록 함수.
 *
 * services 또는 core에서 namespace를 분리해서 사용할 때 사용한다.
 */
export const registerI18nResources = ({
    resources,
    overwrite = true,
}: RegisterI18nResourcesOptions): void => {
    if (!resources) {
        return;
    }

    const i18n = getI18n();

    Object.entries(resources).forEach(([lng, resource]) => {
        const resourceLanguage = resource as ResourceLanguage;

        Object.entries(resourceLanguage).forEach(([ns, data]) => {
            const resourceKey = `${lng}:${ns}`;

            /**
             * 이미 등록되어 있고 overwrite=false라면 skip.
             */
            if (registeredResourceKeys.has(resourceKey) && !overwrite) {
                return;
            }

            i18n.addResourceBundle(lng, ns, data, true, overwrite);

            registeredResourceKeys.add(resourceKey);
        });
    });
};

/**
 * flat 구조 resource 등록 함수.
 *
 * feature package는 이 함수만 사용한다.
 *
 * feature는 namespace를 직접 관리하지 않고,
 * 기본적으로 translation namespace에 key를 flat하게 합친다.
 */
export const registerFlatI18nResources = ({
    resources,
    ns = DEFAULT_NS,
    overwrite = true,
}: RegisterFlatI18nResourcesOptions): void => {
    if (!resources) {
        return;
    }

    const i18n = getI18n();

    Object.entries(resources).forEach(([lng, resource]) => {
        const resourceKey = `${lng}:${ns}`;

        if (registeredResourceKeys.has(resourceKey) && !overwrite) {
            return;
        }

        i18n.addResourceBundle(lng, ns, resource, true, overwrite);

        registeredResourceKeys.add(resourceKey);
    });
};

/**
 * 테스트 또는 특수 상황에서 resource 등록 cache를 초기화할 때 사용.
 *
 * 운영 코드에서는 일반적으로 사용할 일이 없다.
 */
export const clearRegisteredI18nResourceCache = (): void => {
    registeredResourceKeys.clear();
};
